import { lazy } from 'react';
import {
  IconShip,
  IconSettings,
  IconList,
  IconFolder,
  IconTable,
  IconCalendar,
  IconListDetails,
  IconDatabase,
  IconClipboardCheck,
  IconAlertTriangle,
  IconFileCertificate,
  IconCertificate,
  IconFileCheck,
} from '@tabler/icons-react';
import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { ProjectsMenuController } from './controllers/ProjectsMenuController';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import { canManageCustomers } from '../common/customerPortal';
import { registerMwsWelcomeExtensions } from './extensions/MwsWelcomeExtension';
import { registerDriveDomainManagePolicy } from '@ayocore/platform/filemanager/client/components';
import { defineDriveActions } from '@ayocore/platform/filemanager/client/registry/defineDriveActions';
import { defineDriveDecorations } from '@ayocore/platform/filemanager/client/registry/defineDriveDecorations';
import { isCurrentWeekFolderName } from '../common/weekFolderMatch';
import { InspectionPhotoNamingModal } from './components/Inspections/InspectionPhotoNamingModal';

/**
 * Клиентская регистрация модуля MWS.
 *
 * Группа «Проекты»: «Все проекты» (таблица) + динамические пункты-проекты,
 * которые дорегистрирует ProjectsMenuController после логина.
 * Группа MWS: рабочие экраны + Настройки (справочники).
 */
export async function setup(app: AppRuntime): Promise<void> {
  const mws = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
    navigationOrder: 20,
  });

  // order 0 → вес 20*100+0 = 2000 < группы MWS 2001 → отображается ПЕРВОЙ
  const groupProjects = mws.createNavigationGroup({
    key: 'projects',
    label: 'Проекты',
    icon: IconFolder,
    order: 0,
  });

  const groupMws = mws.createNavigationGroup({
    key: 'mws',
    label: 'MWS',
    icon: IconShip,
    order: 1,
  });

  mws.useNavigation([
    {
      group: groupMws,
      icon: IconShip,
      label: 'Заказчики',
      url: '/mws/customers',
      component: lazy(() => import('./components/Customers/CustomerList')),
      guard: () => canManageCustomers(AuthController.getInstance().getSnapshot().user?.roles),
    },
    // Инспекции (M3)
    {
      group: groupMws,
      icon: IconClipboardCheck,
      label: 'Инспекции',
      url: '/mws/inspections',
      component: lazy(() => import('./components/Inspections/InspectionList')),
    },
    // Просмотр инспекции (скрыт из меню)
    {
      icon: IconClipboardCheck,
      label: 'Инспекция',
      url: '/mws/inspections/:id',
      component: lazy(() => import('./components/Inspections/InspectionViewPage')),
      hiddenFromMenu: true,
    },
    // NCR (несоответствия)
    {
      group: groupMws,
      icon: IconAlertTriangle,
      label: 'NCR',
      url: '/mws/ncr',
      component: lazy(() => import('./components/Ncr/NcrList')),
    },
    // Просмотр NCR (скрыт из меню)
    {
      icon: IconAlertTriangle,
      label: 'Несоответствие',
      url: '/mws/ncr/:id',
      component: lazy(() => import('./components/Ncr/NcrViewPage')),
      hiddenFromMenu: true,
    },
    // Протоколы контроля (НК + испытания)
    {
      group: groupMws,
      icon: IconFileCertificate,
      label: 'Протоколы',
      url: '/mws/protocols',
      component: lazy(() => import('./components/Protocols/ProtocolList')),
    },
    // Просмотр протокола (скрыт из меню)
    {
      icon: IconFileCertificate,
      label: 'Протокол',
      url: '/mws/protocols/:id',
      component: lazy(() => import('./components/Protocols/ProtocolViewPage')),
      hiddenFromMenu: true,
    },
    // Акты приёмки (от верфи)
    {
      group: groupMws,
      icon: IconFileCheck,
      label: 'Акты',
      url: '/mws/acts',
      component: lazy(() => import('./components/Acts/ActList')),
    },
    // Просмотр акта (скрыт из меню)
    {
      icon: IconFileCheck,
      label: 'Акт приёмки',
      url: '/mws/acts/:id',
      component: lazy(() => import('./components/Acts/ActViewPage')),
      hiddenFromMenu: true,
    },
    // Реестр сертификатов — тот же компонент, что открывается модалкой из
    // дашборда корпуса (см. useCertificateRegistryModal); здесь он глобальный:
    // все корпуса, с фильтром «Проект».
    {
      group: groupMws,
      icon: IconCertificate,
      label: 'Сертификаты',
      url: '/mws/certificates',
      component: lazy(() => import('./components/certificates/CertificateList')),
    },
    // Типы проектов (серии) — конфигуратор: ITP + секции серии
    {
      group: groupMws,
      icon: IconListDetails,
      label: 'Типы проектов',
      url: '/mws/project-types',
      component: lazy(() => import('./components/project-types/ProjectTypeList')),
    },
    // Конфигуратор типа (скрыт из меню)
    {
      icon: IconListDetails,
      label: 'Тип проекта',
      url: '/mws/project-types/:id',
      component: lazy(() => import('./components/project-types/ProjectTypeViewPage')),
      hiddenFromMenu: true,
    },
    // Таблица проектов — первый пункт группы «Проекты» (menuOrder 0,
    // динамические пункты-проекты идут после с menuOrder 1+).
    {
      group: groupProjects,
      icon: IconTable,
      label: 'Все проекты',
      url: '/mws/projects',
      menuOrder: 0,
      component: lazy(() => import('./components/projects/ProjectList')),
    },
    // Детальная карточка проекта (скрыта из меню)
    {
      icon: IconFolder,
      label: 'Проект',
      url: '/mws/projects/:id',
      component: lazy(() => import('./components/projects/ProjectViewPage')),
      hiddenFromMenu: true,
    },
    // Ядро расчётов (M1) — гриды для контроля данных
    {
      group: groupMws,
      icon: IconTable,
      label: 'Матрица (план)',
      url: '/mws/schedule',
      component: lazy(() => import('./components/schedule/ScheduleEntryList')),
    },
    // Rolling Plan (новый конвейер): снапшоты месячных планов верфи
    {
      group: groupMws,
      icon: IconCalendar,
      label: 'Rolling Plan',
      url: '/mws/rp-snapshots',
      component: lazy(() => import('./components/rp/RpSnapshotList')),
    },
    // Просмотр снапшота (скрыт из меню)
    {
      icon: IconCalendar,
      label: 'Снапшот Rolling Plan',
      url: '/mws/rp-snapshots/:id',
      component: lazy(() => import('./components/rp/RpSnapshotViewPage')),
      hiddenFromMenu: true,
    },
    {
      group: groupMws,
      icon: IconCalendar,
      label: 'Версии плана',
      url: '/mws/rolling-plan/versions',
      component: lazy(() => import('./components/rolling-plan/RollingPlanVersionList')),
    },
    // Просмотр версии Rolling Plan (матрица дат), скрыт из меню
    {
      icon: IconCalendar,
      label: 'Версия Rolling Plan',
      url: '/mws/rolling-plan/versions/:id',
      component: lazy(() => import('./components/rolling-plan/RollingPlanVersionViewPage')),
      hiddenFromMenu: true,
    },
    {
      group: groupMws,
      icon: IconListDetails,
      label: 'Этапы плана',
      url: '/mws/rolling-plan/stages',
      component: lazy(() => import('./components/rolling-plan/RollingPlanStageList')),
    },
    {
      group: groupMws,
      icon: IconDatabase,
      label: 'Исходные данные',
      url: '/mws/rolling-plan/raw',
      component: lazy(() => import('./components/rolling-plan/RollingPlanRawList')),
    },
    // Настройки — индексная страница-список справочников
    {
      group: groupMws,
      icon: IconSettings,
      label: 'Настройки',
      url: '/mws/settings',
      component: lazy(() => import('./components/settings/MwsSettingsPage')),
    },
    // Подстраницы справочников (скрыты из меню)
    {
      icon: IconList,
      label: 'Укрупнённые блоки',
      url: '/mws/settings/grand-blocks',
      component: lazy(() => import('./components/settings/GrandBlockList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Секции',
      url: '/mws/settings/sections',
      component: lazy(() => import('./components/settings/SectionList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Этапы постройки',
      url: '/mws/settings/stages',
      component: lazy(() => import('./components/settings/StageList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Коды областей / зон',
      url: '/mws/settings/area-codes',
      component: lazy(() => import('./components/settings/AreaCodeList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Палубы',
      url: '/mws/settings/decks',
      component: lazy(() => import('./components/settings/DeckList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Направления работ',
      url: '/mws/settings/directions',
      component: lazy(() => import('./components/settings/DirectionList')),
      hiddenFromMenu: true,
    },
    {
      icon: IconList,
      label: 'Виды работ',
      url: '/mws/settings/work-types',
      component: lazy(() => import('./components/settings/WorkTypeList')),
      hiddenFromMenu: true,
    },
  ]);

  // Динамические пункты-проекты в группе «Проекты»: контроллер грузит
  // список после логина и дорегистрирует пункты меню.
  ProjectsMenuController.getInstance().init(mws.useNavigation, groupProjects);

  // Карточки проектов на главной (WelcomePage)
  registerMwsWelcomeExtensions();
  // Доменная область фото инспекций: папками внутри mws:inspection:<id> в
  // public_write управляют по праву на инспекцию (кнопки меню Диска);
  // сервер authoritative (canManageScopeFolder).
  registerDriveDomainManagePolicy(code => /^mws:inspection:\d+$/.test(code));
  // Текущая отчётная неделя — подсвечена в дереве и списке «Файлов проекта».
  // Якорь ТОЧНЫЙ (`mws:week:<id>`, без подпапок вроде
  // `mws:week:41:cat:...:protocols`), «текущая ли» — чистая функция от имени
  // папки и даты: декоратор синхронный, запросов делать не может.
  defineDriveDecorations({
    when: folder => /^mws:week:\d+$/.test(folder.code ?? ''),
    decorate: folder =>
      isCurrentWeekFolderName(folder.name)
        ? { accent: 'green', icon: 'check', tooltip: 'Текущая отчётная неделя' }
        : null,
  });
  // «Назвать фото по шаблону» — в меню корня инспекции и её user-подпапок
  // (component-действие: модалку рендерит платформа; право проверяет сервер).
  defineDriveActions({
    when: folder => /^mws:inspection:\d+$/.test(folder.code ?? ''),
    folderActions: [
      {
        id: 'mws.inspection.renamePhotos',
        title: 'Назвать фото по шаблону',
        component: InspectionPhotoNamingModal,
        // Массовое переименование — изменяющая операция: читателю области
        // пункт не показываем (право на саму инспекцию проверяет сервер)
        requiresWrite: true,
      },
    ],
  });
}
